import { useEffect } from "react";
import { Fade, FormControl, TextField } from "@mui/material";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-iplastic";
import "ace-builds/src-noconflict/ext-language_tools";
import "brace/ext/searchbox"; // для команд ctrl+f и ctrl+h
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectActiveRobotId, selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { getScript, selectScript } from "../../../store/ses/sesScriptSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SES } from "../../../constants/accessRights";
import { colorPrimary } from "../../../constants/colors";
import { RequestStatus, ResponseStatus } from "../../../types/statusTypes";
import BlockWithLoading from "../../../ui/BlockWithLoading/BlockWithLoading";
import BlockWithError from "../../../ui/BlockWithError/BlockWithError";
import { IScriptProps } from "./Script.props";
import styles from "./Script.module.scss";
import { Hint } from "@/HOC";
import { selectSesHints } from "@/store/hintsSlice";

const Script = ({ changeFlg, setChangeFlg, selectScriptId, name, setName, code, setCode }: IScriptProps): JSX.Element => {
	const script = useAppSelector(selectScript); // store - скрипт
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const sesHints = useAppSelector(selectSesHints);

	const dispatch = useAppDispatch();

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за полученным скриптом
	useEffect(() => {
		setName(script.name); // вписываем в поле имени
		setCode(script.script); // вписываем в поле для кода
	}, [script]);

	return (
		<div className={styles.scriptCode}>
			{/* загрузка скрипта */}
			{script.status === RequestStatus.LOADING && <BlockWithLoading />}

			{/* ошибка получения скрипта */}
			{(script.status === RequestStatus.FAILED || script.error === ResponseStatus.FAILED) && (
				<BlockWithError message={script.message} updateHandler={() => activeRobotId && dispatch(getScript({ robotId: activeRobotId, scriptId: selectScriptId }))} />
			)}

			{/* скрипт */}
			{script.status === RequestStatus.IDLE && (
				<Fade in={true} timeout={500}>
					<div className={styles.scriptCodeWrapper}>
						{/* название скрипта */}
						{(script.type === "endpoint" || script.type === "data") && (
							<FormControl margin="dense">
								<Hint hint={sesHints.hint_sesFieldScriptName} position="left" offset={[0, -8]}>
									<TextField
										label={translate("input_name")}
										variant="outlined"
										disabled={!isAccess(SES.SCRIPT_EDIT) || activeRobotVersion !== "draft"}
										value={name}
										fullWidth
										onChange={(e) => {
											setName(e.target.value);
											(!changeFlg.thisIs || !changeFlg.listOfChanges.includes("name")) &&
												setChangeFlg((prev) => ({ thisIs: true, listOfChanges: [...prev.listOfChanges, "name"] }));
										}}
										InputProps={{
											style: {
												height: 33,
												fontSize: 13,
												color: colorPrimary,
											},
										}}
										InputLabelProps={{
											style: {
												fontSize: 13,
											},
										}}
										sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
									/>
								</Hint>
							</FormControl>
						)}

						{/* код */}
						<AceEditor
							name="script"
							placeholder={translate("title_writeCode")}
							mode="python"
							theme="iplastic"
							width="100%"
							fontSize={11}
							value={code}
							onChange={(value) => {
								setCode(value);
								(!changeFlg.thisIs || !changeFlg.listOfChanges.includes("code")) &&
									setChangeFlg((prev) => ({ thisIs: true, listOfChanges: [...prev.listOfChanges, "code"] }));
							}}
							editorProps={{ $blockScrolling: true }}
							setOptions={{
								enableBasicAutocompletion: true,
								enableLiveAutocompletion: true,
								// enableSnippets: true,
							}}
							style={{ flexGrow: 1 }}
							readOnly={!isAccess(SES.SCRIPT_EDIT) || activeRobotVersion !== "draft"}
						/>
					</div>
				</Fade>
			)}
		</div>
	);
};

export default Script;
